import { createContext, createElement, FunctionComponent, useCallback, useContext, useEffect, useState } from "react";
import { type CognitoUser } from "@aws-amplify/auth";
import { type AuthChallengeName } from "@aws-amplify/ui";
import * as Sentry from "@sentry/react";
import { Auth } from "aws-amplify";
import { Client, CurrentUserQuery } from "src/graphql/generated/graphql";
import { resetPasswordMutation } from "src/graphql/operations/auth/mutations";
import { currentUserQuery } from "src/graphql/operations/auth/query";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";
import { RoutePath } from "src/routes";
import { handleError } from "src/utils/error";

import { useNavigation } from "./useNavigation";

export type UserAuth = CurrentUserQuery["currentUser"];

export interface AuthOptions {
    onSuccess?(user: UserAuth);
    onError?(err: AuthChallengeName | any);
}

export interface AuthContext {
    isAuthenticated: boolean;
    isAuthenticating: boolean;
    cognitoUser: CognitoUser | null;
    user: UserAuth | null;
    client: Client | null;
    setIsAuthenticating(isAuthenticating: boolean);
    setCognitoUser(cognitoUser: CognitoUser);
    setClient(client: Client);
    handleCognitoUser(cognitoUser: CognitoUser, options: AuthOptions);
    initResetPassword(email: string): Promise<void>;
    reloadUser(): Promise<void>;
    resetUser(): void;
}

const authContext = createContext<AuthContext>({
    isAuthenticated: false,
    isAuthenticating: true,
    cognitoUser: null,
    user: null,
    client: null,
    setIsAuthenticating() {},
    setCognitoUser() {},
    setClient() {},
    async handleCognitoUser() {},
    async initResetPassword() {},
    async reloadUser() {},
    resetUser() {},
});

const setSentryUser = (userId?: string, client?: Client) => {
    if (!userId) {
        Sentry.setUser(null);
        return;
    }

    Sentry.setUser({
        id: userId,
    });

    if (client) {
        Sentry.setContext("client", {
            id: client.id,
            name: client.name,
            role: client.role,
            tenant: client.tenant?.id ?? client.tenant?.tenantId ?? client.tenant?.domainName,
        });
    }
};

export const withAuth = <Props>(Component: FunctionComponent<Props>) => {
    return (props: Props) => {
        const [isAuthenticated, setIsAuthenticated] = useState(false);
        const [isAuthenticating, setIsAuthenticating] = useState(false);
        const [cognitoUser, setCognitoUser] = useState<CognitoUser>();
        const [user, setUser] = useState<UserAuth | null>(null);
        const [client, setClient] = useState<Client | null>(null);

        // Custom setter for client that also updates Sentry
        const updateClient = useCallback(
            (newClient: Client) => {
                setClient(newClient);
                setSentryUser(user?.id, newClient);
            },
            [user]
        );

        const loadUser = useCallback(
            async (options: AuthOptions = {}) => {
                try {
                    const response = await graphqlFetch(currentUserQuery);

                    const currentUser = response.currentUser;
                    setUser(currentUser);
                    setIsAuthenticated(true);

                    setSentryUser(currentUser?.id, client);
                    options?.onSuccess?.(currentUser);
                } catch (err) {
                    setUser(null);
                    setIsAuthenticated(false);

                    setSentryUser(null);
                    options?.onError?.(err);

                    handleError(err, { context: { clientId: client?.id }, silent: true });
                } finally {
                    setIsAuthenticating(false);
                }
            },
            [client]
        );

        const resetUser = useCallback(() => {
            setUser(null);
            setIsAuthenticated(false);
            setCognitoUser(null);
            setClient(null);
            setSentryUser(null);
            setIsAuthenticating(false);
        }, []);

        const handleCognitoUser = useCallback(
            (cognitoUser: CognitoUser, options: AuthOptions) => {
                const isValid = cognitoUser?.getSignInUserSession?.().isValid?.();
                // const idToken = cognitoUser?.getSignInUserSession?.().getIdToken?.();
                const accessToken = cognitoUser?.getSignInUserSession?.().getAccessToken?.();
                // const refreshToken = cognitoUser?.getSignInUserSession?.().getRefreshToken?.();

                if (isValid && accessToken) {
                    loadUser(options);
                    return;
                }

                options?.onError?.(null);
            },
            [loadUser]
        );

        const handleAutoLogin = useCallback(async () => {
            try {
                const cognitoUser: CognitoUser = await Auth.currentAuthenticatedUser();
                handleCognitoUser(cognitoUser, {});
            } catch (err) {}
        }, [handleCognitoUser]);

        const initResetPassword = useCallback(async (email: string) => {
            try {
                await graphqlFetch(
                    resetPasswordMutation,
                    { email: String(email).toLowerCase().trim() },
                    { authMode: "API_KEY" }
                );
            } catch (err) {
                handleError(err, { silent: true });
                throw err;
            }
        }, []);

        useEffect(() => {
            if (client) {
                return;
            }

            handleAutoLogin();
        }, [client, handleAutoLogin]);

        return createElement(
            authContext.Provider,
            {
                value: {
                    isAuthenticated,
                    isAuthenticating,
                    user,
                    cognitoUser,
                    client,
                    setIsAuthenticating,
                    setCognitoUser,
                    setClient: updateClient, // Use our custom setter that updates Sentry
                    handleCognitoUser,
                    initResetPassword,
                    reloadUser: loadUser,
                    resetUser,
                },
            },
            createElement(Component, props)
        );
    };
};

export const useAuthContext = () => useContext(authContext);

export interface LoginOptions extends AuthOptions {
    remember?: boolean;
}

export interface LoginResponse {
    accessToken: string;
    user: UserAuth;
}

export const useLogin = () => {
    const auth = useAuthContext();

    const login = useCallback(async (username: string, password: string, options: LoginOptions = {}) => {
        auth.setIsAuthenticating(true);

        try {
            const cognitoUser: CognitoUser = await Auth.signIn(username, password);

            auth.setCognitoUser(cognitoUser);

            if (cognitoUser?.challengeName) {
                options?.onError?.(cognitoUser.challengeName);
                return;
            }

            auth.handleCognitoUser(cognitoUser, options);
        } catch (err) {
            handleError(err, { silent: true });
            options?.onError?.(err);
        } finally {
            auth.setIsAuthenticating(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        auth,
        login,
    };
};

export const useLogout = () => {
    const auth = useAuthContext();
    const { navigateTo } = useNavigation();

    const logout = useCallback(async () => {
        await Auth.signOut({});
        auth.resetUser();
        navigateTo(RoutePath.LOGIN);
    }, [auth, navigateTo]);

    return {
        auth,
        logout,
    };
};

export interface CompleteNewPasswordOptions extends AuthOptions {}

export const useCompleteNewPassword = () => {
    const auth = useAuthContext();

    const completeNewPassword = useCallback(
        async (newPassword: string, options?: CompleteNewPasswordOptions) => {
            if (!auth?.cognitoUser) {
                handleError(new Error("cannot complete new password without cognitoUser"), { silent: true });
                options?.onError?.(null);
                return;
            }

            auth.setIsAuthenticating(true);

            try {
                const newCognitoUser = await Auth.completeNewPassword(auth.cognitoUser, newPassword);

                auth.setCognitoUser(newCognitoUser);

                if (newCognitoUser?.challengeName) {
                    options?.onError?.(newCognitoUser.challengeName);
                    return;
                }

                auth.handleCognitoUser(auth.cognitoUser, options);
            } catch (err) {
                handleError(err);
                options?.onError?.(err);
            } finally {
                auth.setIsAuthenticating(false);
            }
        },
        [auth]
    );

    return {
        auth,
        completeNewPassword,
    };
};
